import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import MDEditor from '@uiw/react-md-editor';
import { vendorProfileService, VendorProfile, VendorLocale } from '../../services/vendorProfileService';

interface ServicePolicyEditPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedData: Partial<VendorProfile>) => void;
  vendorProfile: VendorProfile | null;
}

const ServicePolicyEditPopup: React.FC<ServicePolicyEditPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorProfile
}) => {
  const [acceptOrdersDeadline, setAcceptOrdersDeadline] = useState('');
  const [acceptChangesDeadline, setAcceptChangesDeadline] = useState('');
  const [policyText, setPolicyText] = useState('');
  const [currentLang, setCurrentLang] = useState<'en' | 'ar'>('en');
  const [locale, setLocale] = useState<VendorLocale>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with current vendor data when opened or vendorProfile changes
  useEffect(() => {
    if (vendorProfile && open) {
      // Initialize locale: if backend has no locale yet, seed English from top-level servicePolicy
      const initialLocale: VendorLocale =
        vendorProfile.locale ??
        (vendorProfile.servicePolicy
          ? {
              en: {
                servicePolicy: String(vendorProfile.servicePolicy),
              },
            }
          : {});

      setLocale(initialLocale);
      setCurrentLang('en');

      const acceptOrdersDeadlineValue = vendorProfile.acceptOrdersDeadline !== undefined
        ? String(vendorProfile.acceptOrdersDeadline)
        : '';
      const acceptChangesDeadlineValue = vendorProfile.acceptChangesDeadline !== undefined
        ? String(vendorProfile.acceptChangesDeadline)
        : '';
      const enEntry = initialLocale.en || {};
      const policyTextValue =
        enEntry.servicePolicy ??
        (vendorProfile.servicePolicy ? String(vendorProfile.servicePolicy) : '');
      
      setAcceptOrdersDeadline(acceptOrdersDeadlineValue);
      setAcceptChangesDeadline(acceptChangesDeadlineValue);
      setPolicyText(policyTextValue);
    }
  }, [vendorProfile, open]);

  const handleLanguageChange = useCallback(
    (newLang: 'en' | 'ar') => {
      const trimmedPolicy = policyText.trim();
      const nextLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(trimmedPolicy ? { servicePolicy: trimmedPolicy } : {}),
        },
      };

      setLocale(nextLocale);
      setCurrentLang(newLang);

      const targetEntry = nextLocale[newLang] || {};
      setPolicyText(
        targetEntry.servicePolicy ||
          (newLang === 'en' && vendorProfile?.servicePolicy
            ? String(vendorProfile.servicePolicy)
            : '') ||
          ''
      );
    },
    [currentLang, locale, policyText, vendorProfile]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vendorProfile) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    // Validate that deadline fields contain only numbers
    const ordersDeadline = parseInt(acceptOrdersDeadline);
    const changesDeadline = parseInt(acceptChangesDeadline);

    if (acceptOrdersDeadline && (isNaN(ordersDeadline) || ordersDeadline < 0)) {
      toast.error('Accept orders deadline must be a valid positive number');
      return;
    }

    if (acceptChangesDeadline && (isNaN(changesDeadline) || changesDeadline < 0)) {
      toast.error('Accept changes deadline must be a valid positive number');
      return;
    }

    setIsSubmitting(true);

    try {
      const finalLocale: VendorLocale = {
        ...locale,
        [currentLang]: {
          ...(locale[currentLang] || {}),
          ...(policyText.trim() ? { servicePolicy: policyText.trim() } : {}),
        },
      };

      const defaultServicePolicy =
        finalLocale.en?.servicePolicy ??
        (currentLang === 'en'
          ? policyText.trim()
          : vendorProfile.servicePolicy
          ? String(vendorProfile.servicePolicy)
          : policyText.trim());

      const editData = {
        acceptOrdersDeadline: acceptOrdersDeadline ? ordersDeadline : undefined,
        acceptChangesDeadline: acceptChangesDeadline ? changesDeadline : undefined,
        servicePolicy: defaultServicePolicy,
        locale: finalLocale,
      };
      const response = await vendorProfileService.editProfile(
        vendorProfile.vendorId, 
        editData,
      );

      if (response.success) {
        toast.success('Service policy updated successfully!');
        onUpdate(editData);
        handleClose();
      } else {
        toast.error(response.message || 'Failed to update service policy');
      }
    } catch (error: any) {
      console.error('Error updating service policy:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update service policy. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 700,
        fontSize: '20px',
        color: '#181818'
      }}>
        Edit Service Policy
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            py: 2
          }}
        >
          {/* Language selector */}
          <FormControl fullWidth size="small">
            <Select
              value={currentLang}
              onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'ar')}
              displayEmpty
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ar">Arabic</MenuItem>
            </Select>
          </FormControl>
          {/* Accept Orders Deadline Field */}
          <TextField
            fullWidth
            type="number"
            placeholder="Accept orders not later than (hours)"
            value={acceptOrdersDeadline}
            onChange={(e) => setAcceptOrdersDeadline(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: acceptOrdersDeadline ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Accept Changes Deadline Field */}
          <TextField
            fullWidth
            type="number"
            placeholder="Accept changes not later than (hours)"
            value={acceptChangesDeadline}
            onChange={(e) => setAcceptChangesDeadline(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: acceptChangesDeadline ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Policy Text Field - Markdown Editor */}
          <Box sx={{ width: '100%' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '14px',
                color: '#181818',
                fontWeight: 500
              }}
            >
              Service Policy (detailed terms and conditions)
            </Typography>
            <Box
              data-color-mode="light"
              sx={{
                '& .w-md-editor': {
                  backgroundColor: '#f6f8f8',
                  borderRadius: '12px',
                  border: 'none',
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                },
                '& .w-md-editor-text-pre': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: '16px',
                },
                '& .w-md-editor-text': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: '16px',
                },
                '& .w-md-editor-preview': {
                  fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: '16px',
                }
              }}
            >
              <MDEditor
                value={policyText}
                onChange={(value) => setPolicyText(value || '')}
                preview="edit"
                hideToolbar={false}
                visibleDragbar={false}
                height={300}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Service Policy'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ServicePolicyEditPopup;
